import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { BodyMedium, TitleLarge, TitleMedium } from '../../components/Texts';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TextInput from '../../components/Inputs/Text';
import ImageInput from './ImageInput';
import Button from '../../components/Button';
import { useSubmitTokenMutation } from '../../redux/api/authorized/tokenSubmission';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useSelector } from 'react-redux';
import { usePrivy } from '@privy-io/react-auth';
import useCustomLogin from '../../hooks/useCustomLogin';

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
`;

const FormColumn = styled.div`
  display: flex;
  width: 100%;
  gap: 15px;
  flex-direction: column;
`;

const schema = yup
  .object({
    tokenAddress: yup.string().required('Mint address is required'),
    network: yup.string().required(),
    projectURI: yup
      .string('Invalid Website, Twitter or Telegram')
      .url('Website, Twitter or Telegram must be an URL')
      .required('Website, Twitter or Telegram is required'),
    logoImage: yup.string().required('Logo is required'),
    giftCardImage: yup.string().required('Gift card image is required'),
  })
  .required();

const TokenSubmission = () => {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      network: 'solana',
    },
  });

  const privyToken = useSelector(selectPrivyToken);
  const { login } = useCustomLogin();

  const [submitToken] = useSubmitTokenMutation();

  const navigate = useNavigate();

  const onSubmit = async (data) => {
    if (!privyToken) {
      login();
    } else {
      const response = await submitToken(data);
      if (response?.error) {
        toast('There was an error sending the token submission.', {
          type: 'error',
        });
      } else {
        navigate('/');
        toast('Token submission created.', {
          type: 'success',
        });
      }
    }
  };

  const theme = useTheme();

  return (
    <>
      <Container>
        <TitleLarge>Token Submission</TitleLarge>
        <TitleMedium>Submit a token to get listed on Deks.xyz</TitleMedium>
        <BodyMedium>
          You will get an email notification when we reject or approve your
          request
        </BodyMedium>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            marginTop: 40,
            gap: 40,
            display: 'flex',
            flexDirection: 'column',
            padding: 24,
            borderRadius: 16,
            border: `1px solid ${theme.border.primary}`,
          }}
        >
          <FormContainer>
            <FormColumn>
              <Controller
                name='tokenAddress'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Mint address'
                    type='text'
                    error={errors?.tokenAddress?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name='network'
                control={control}
                render={({ field }) => (
                  <TextInput
                    disabled
                    label='Network'
                    type='text'
                    error={errors?.network?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name='projectURI'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Website, Telegram or Twitter'
                    type='text'
                    error={errors?.projectURI?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name='giftCardImage'
                control={control}
                render={({ field }) => (
                  <ImageInput
                    aspectRatio={814 / 452}
                    label='Gift card'
                    value={field.value}
                    onChange={field.onChange}
                    error={errors?.giftCardImage?.message}
                    {...field}
                  />
                )}
              />
            </FormColumn>
            <FormColumn>
              <Controller
                name='logoImage'
                control={control}
                render={({ field }) => (
                  <ImageInput
                    aspectRatio={1}
                    circularCrop={true}
                    label='Logo'
                    value={field.value}
                    onChange={field.onChange}
                    error={errors?.logoImage?.message}
                    {...field}
                  />
                )}
              />
            </FormColumn>
          </FormContainer>
          <Button loading={isSubmitting} disabled={!isValid || isSubmitting}>
            Submit Token
          </Button>
        </form>
      </Container>
    </>
  );
};

export default TokenSubmission;
